import { z } from 'zod';

/**
 * Esquema para validação dos serviços selecionados
 */
export const serviceSchema = z.object({
  id: z.string(),
  nome: z.string(),
  preco: z.number(),
  duracao: z.number(),
  categoria: z.string()
}).or(z.string());

/**
 * Esquema para validação de profissional
 */
export const professionalSchema = z.object({
  id: z.string(),
  nome: z.string()
}).nullable();

/**
 * Esquema para validação dos dados do cliente
 */
export const clientSchema = z.object({
  name: z.string().min(3, 'Nome deve ter pelo menos 3 caracteres'),
  phone: z.string().min(10, 'Telefone inválido').max(15),
  email: z.string().email('Email inválido').or(z.string().length(0).optional()),
  notes: z.string().optional()
});

/**
 * Esquema de validação para o formulário de agendamento
 * Utiliza Zod para validação de dados e integração com React Hook Form
 */
export const bookingSchema = z.object({
  services: z.array(z.any()).min(1, { message: 'Selecione pelo menos um serviço' }),
  date: z.date({ required_error: 'Selecione uma data' }).nullable().refine(val => val !== null, {
    message: 'Selecione uma data para o agendamento'
  }),
  time: z.string({ required_error: 'Selecione um horário' }).nullable().refine(val => val !== null, {
    message: 'Selecione um horário para o agendamento'
  }),
  professional: z.any({ required_error: 'Selecione um profissional' }).nullable().refine(val => val !== null, {
    message: 'Selecione um profissional para o agendamento'
  }),
  client: z.object({
    name: z.string().min(3, { message: 'Nome deve ter pelo menos 3 caracteres' }),
    phone: z.string().min(10, { message: 'Telefone inválido' })
      .regex(/^\(?([0-9]{2})\)?[-. ]?([0-9]{4,5})[-. ]?([0-9]{4})$/, { 
        message: 'Formato de telefone inválido. Use (99) 99999-9999' 
      }),
    email: z.string().email({ message: 'E-mail inválido' }).optional().or(z.literal('')),
    notes: z.string().optional().or(z.literal(''))
  })
});

/**
 * Esquema para a etapa 1 (Seleção de Serviços)
 */
export const serviceSelectionSchema = z.object({
  services: z.array(serviceSchema).min(1, 'Selecione pelo menos um serviço')
});

/**
 * Esquema para a etapa 2 (Seleção de Data/Hora/Profissional)
 */
export const dateTimeSelectionSchema = z.object({
  date: z.date({
    required_error: 'Selecione uma data',
    invalid_type_error: 'Data inválida'
  }).nullable(),
  time: z.string({
    required_error: 'Selecione um horário'
  }).nullable(),
  professional: professionalSchema
});

/**
 * Função helper para validar um objeto contra um schema
 * @param {Object} schema - Esquema Zod
 * @param {Object} values - Valores a serem validados
 * @returns {Object} - Resultado da validação (success, errors)
 */
export const validateSchema = (schema, values) => {
  try {
    schema.parse(values);
    return { success: true, errors: null };
  } catch (error) {
    const formattedErrors = {};
    if (error instanceof z.ZodError) {
      error.errors.forEach(err => {
        const path = err.path.join('.');
        formattedErrors[path] = err.message;
      });
    }
    return { success: false, errors: formattedErrors };
  }
};

/**
 * Schema estendido para o formulário administrativo
 * Adiciona campos específicos do admin ao schema padrão
 */
export const adminBookingSchema = bookingSchema.extend({
  confirmed: z.boolean().optional(),
  paymentConfirmed: z.boolean().optional(),
  skipAvailabilityCheck: z.boolean().optional(),
  valueOverride: z.number().optional()
}); 